import Icon from "@mdi/react";
import {
  mdiAlertOutline,
  mdiClose,
  mdiCheck,
  mdiPackageVariantClosed,
  mdiFileDocumentOutline,
  mdiAlertCircle,
  mdiFormatListNumbered,
  mdiCurrencyInr,
} from "@mdi/js";
import React, { useState, useEffect } from "react";
import "../inward-styles.css";
import {
  ItemWarehouseModel,
  WarehouseInwardModel,
} from "../model/warehouse_inward_models";

export interface DisputeData {
  disputeTypes: string[];
  description: string;
  selectedItems: string[];
}

const getDisputeItemRowKey = (
  item: ItemWarehouseModel,
  index: number,
): string => {
  return `${item.inventoryItemCode}__${item.itemPrice}__${index}`;
};

const disputeOptions = [
  {
    id: "quantity_mismatch",
    label: "Quantity Mismatch",
    description: "PO, Invoice, or Physical quantities do not match",
    icon: mdiFormatListNumbered,
  },
  {
    id: "rate_mismatch",
    label: "Rate Mismatch",
    description: "Invoice rate differs from PO rate",
    icon: mdiCurrencyInr,
  },
  {
    id: "quality_issue",
    label: "Quality Issue",
    description: "Damaged, defective, or wrong specifications",
    icon: mdiPackageVariantClosed,
  },
  {
    id: "documentation_issue",
    label: "Documentation Issue",
    description: "Missing or incorrect documents",
    icon: mdiFileDocumentOutline,
  },
];

interface RaiseDisputeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: DisputeData) => void;
  inwardOrder: WarehouseInwardModel;
  invoiceQtys: Record<string, number>;
  invoiceRates: Record<string, number>;
  physicalQtys: Record<string, number>;
  deviceSelections: Record<string, any>;
  isSubmitting?: boolean;
}

const RaiseDisputeModal: React.FC<RaiseDisputeModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  inwardOrder,
  invoiceQtys,
  invoiceRates,
  physicalQtys,
  deviceSelections,
  isSubmitting = false,
}) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [disputeTypes, setDisputeTypes] = useState<string[]>([""]);
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      const mismatchItems =
        inwardOrder.items?.reduce<string[]>((acc, item, index) => {
          const rowKey = getDisputeItemRowKey(item, index);
          const requested = item.requestedQty || 0;
          const invQty = invoiceQtys[rowKey] || 0;
          const invRate = invoiceRates[rowKey] || 0;
          const physQty =
            item.subCategory === "Finished Goods"
              ? deviceSelections[rowKey]?.matched.length || 0
              : physicalQtys[rowKey] || 0;

          const hasMismatch =
            invQty !== requested ||
            invRate !== item.itemPrice ||
            physQty !== requested;

          if (hasMismatch) {
            acc.push(rowKey);
          }

          return acc;
        }, []) || [];

      setSelectedItems(mismatchItems);
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, inwardOrder.items]);

  if (!isOpen) return null;

  const toggleItemSelection = (rowKey: string) => {
    setSelectedItems((prev) =>
      prev.includes(rowKey)
        ? prev.filter((code) => code !== rowKey)
        : [...prev, rowKey],
    );
  };

  const toggleAll = () => {
    if (selectedItems.length === (inwardOrder.items?.length || 0)) {
      setSelectedItems([]);
    } else {
      setSelectedItems(
        inwardOrder.items?.map((item, index) =>
          getDisputeItemRowKey(item, index),
        ) || [],
      );
    }
  };

  const handleSubmit = () => {
    if (selectedItems.length === 0) {
      setError("Please select at least one item to dispute");
      return;
    }

    onSubmit({
      disputeTypes,
      description,
      selectedItems,
    });
  };

  const toggleDisputeType = (typeId: string) => {
    setDisputeTypes((prev) =>
      prev.includes(typeId)
        ? prev.filter((id) => id !== typeId)
        : [...prev, typeId],
    );
  };

  return (
    <div
      id="warehouse-inward-raise-dispute-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 overflow-y-auto"
    >
      <div className="relative my-auto flex h-max max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border bg-white shadow-lg animate-in fade-in zoom-in duration-200 wh-modal-xs">
        {/* Header */}
        <div className="flex items-center justify-between border-b p-4 flex-shrink-0 bg-white z-10">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <Icon path={mdiAlertOutline} size={1} className="text-yellow-500" />
            Raise Dispute — {inwardOrder.inward_order_id}
          </h2>
          <button
            id="warehouse-inward-raise-dispute-modal-close"
            onClick={onClose}
            className="rounded-full p-1 hover:bg-gray-100 transition-colors"
          >
            <Icon path={mdiClose} size={1} />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 min-h-0">
          {/* Order Info */}
          <div className="rounded-lg bg-gray-50 p-3">
            <p className="font-semibold text-gray-900">
              {inwardOrder.inward_order_id}
            </p>
            <p className="text-sm text-gray-500">
              Vendor: {inwardOrder.from?.name || "N/A"}
            </p>
          </div>

          {/* Item Selection Table */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Select Disputed Items
            </label>
            <p className="text-xs text-gray-500">
              All items will be quarantined. Select the items that have issues.
            </p>

            <div className="rounded-lg border overflow-x-auto">
              <table className="w-full text-sm text-left min-w-[600px]">
                <thead className="bg-gray-50 border-b sticky top-0 z-10">
                  <tr>
                    <th className="p-3 w-10">
                      <button
                        id="warehouse-inward-raise-dispute-select-all"
                        onClick={toggleAll}
                        className={`h-4 w-4 rounded border flex items-center justify-center transition-colors ${
                          selectedItems.length === inwardOrder.items?.length
                            ? "bg-blue-600 border-blue-600 text-white"
                            : "bg-white border-gray-300"
                        }`}
                      >
                        {selectedItems.length === inwardOrder.items?.length && (
                          <Icon path={mdiCheck} size={0.5} />
                        )}
                      </button>
                    </th>
                    <th className="p-3 font-medium text-gray-600">Item</th>
                    <th className="p-3 font-medium text-gray-600 text-center">
                      PO Qty
                    </th>
                    <th className="p-3 font-medium text-gray-600 text-center">
                      Inv Qty
                    </th>
                    <th className="p-3 font-medium text-gray-600 text-center">
                      Phys Qty
                    </th>
                    <th className="p-3 font-medium text-gray-600 text-right">
                      PO Rate
                    </th>
                    <th className="p-3 font-medium text-gray-600 text-right">
                      Inv Rate
                    </th>
                    <th className="p-3 font-medium text-gray-600 w-10">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y bg-white">
                  {inwardOrder.items?.map((item, index) => {
                    const rowKey = getDisputeItemRowKey(item, index);
                    const isSelected = selectedItems.includes(rowKey);
                    const invQty = invoiceQtys[rowKey] || 0;
                    const invRate = invoiceRates[rowKey] || 0;
                    const physQty =
                      item.subCategory === "Finished Goods"
                        ? deviceSelections[rowKey]?.matched.length || 0
                        : physicalQtys[rowKey] || 0;

                    const hasMismatch =
                      invQty !== item.requestedQty ||
                      invRate !== item.itemPrice ||
                      physQty !== item.requestedQty;

                    return (
                      <tr
                        key={rowKey}
                        className={`transition-colors ${isSelected ? "bg-yellow-50/50" : "hover:bg-gray-50"}`}
                      >
                        <td className="p-3">
                          <button
                            id={`warehouse-inward-raise-dispute-item-${rowKey}`}
                            onClick={() => toggleItemSelection(rowKey)}
                            className={`h-4 w-4 rounded border flex items-center justify-center transition-colors ${
                              isSelected
                                ? "bg-blue-600 border-blue-600 text-white"
                                : "bg-white border-gray-300"
                            }`}
                          >
                            {isSelected && <Icon path={mdiCheck} size={0.5} />}
                          </button>
                        </td>
                        <td className="p-3">
                          <p className="font-medium text-gray-900">
                            {item.itemName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {item.inventoryItemCode}
                          </p>
                        </td>
                        <td className="p-3 text-center font-medium text-yellow-600">
                          {item.requestedQty}
                        </td>
                        <td className="p-3 text-center font-medium text-yellow-600">
                          {invQty}
                        </td>
                        <td className="p-3 text-center font-medium text-yellow-600">
                          {physQty}
                        </td>
                        <td className="p-3 text-right">₹{item.itemPrice}</td>
                        <td className="p-3 text-right">₹{invRate}</td>
                        <td className="p-3">
                          {hasMismatch && (
                            <Icon
                              path={mdiAlertCircle}
                              size={0.6}
                              className="text-yellow-500"
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500">
              {selectedItems.length} of {inwardOrder.items?.length || 0} item(s)
              selected as disputed
            </p>
          </div>

          {/* Dispute Type */}
          <div className="space-y-4">
            <label className="text-sm font-medium text-gray-700">
              Dispute Type
            </label>
            <div className="grid gap-3">
              {disputeOptions.map((option) => {
                const isSelected = disputeTypes.includes(option.id);
                return (
                  <div
                    key={option.id}
                    id={`warehouse-inward-raise-dispute-type-${option.id}`}
                    onClick={() => toggleDisputeType(option.id)}
                    className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-all ${
                      isSelected
                        ? "border-blue-600 bg-blue-50 ring-1 ring-blue-600"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <div className="mt-1">
                      <div
                        className={`flex h-4 w-4 items-center justify-center rounded border transition-colors ${
                          isSelected
                            ? "bg-blue-600 border-blue-600 text-white"
                            : "bg-white border-gray-300"
                        }`}
                      >
                        {isSelected && <Icon path={mdiCheck} size={0.5} />}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Icon path={option.icon} size={0.7} />
                        <span
                          className={`text-sm font-semibold ${isSelected ? "text-blue-900" : "text-gray-900"}`}
                        >
                          {option.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        {option.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="warehouse-inward-raise-dispute-description"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the issue in detail..."
              className="w-full rounded-md border border-gray-300 p-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all"
            />
          </div>

          {error && (
            <p className="text-sm text-red-500 font-medium animate-in fade-in slide-in-from-top-1">
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t p-4 bg-gray-50 flex-shrink-0">
          <button
            id="warehouse-inward-raise-dispute-cancel"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            id="warehouse-inward-raise-dispute-submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-all active:scale-95 ${
              isSubmitting
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-blue-700"
            }`}
          >
            <Icon path={mdiAlertOutline} size={0.7} />
            {isSubmitting ? "Submitting..." : "Submit Dispute"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RaiseDisputeModal;
